import { Fragment, type ReactNode } from 'react'
import { ThemeProvider } from '@/lib/theme'
import { toLocale, useI18n } from '@/lib/i18n'
import Header from '@/components/layout/Header'
import Footer from '@/components/layout/Footer'
import { useDocumentMeta } from '@/lib/useDocumentMeta'
import { PAGES } from '@/lib/seo'
import '@/styles/imprint.css'

// Company data as registered: Firmenbuch, and GISA for the trade licences
// (both active since 15.09.2025, location 1150 Wien). The authority and the
// WKO group follow from those: Vienna's district office for the location,
// and UBIT for both trades.
const COMPANY = {
  firm: 'SoftMaze Solutions GmbH',
  fn: 'FN 661396 t',
  vat: 'ATU82581369',
  director: 'Parham Jabari',
  phone: '+43 660 5369991',
  email: 'info@softmaze.at',
}

type Row = [string, ReactNode]

// Gewerbeordnung 1994 in the federal legal information system (RIS).
const GEWO_URL = 'https://www.ris.bka.gv.at/GeltendeFassung.wxe?Abfrage=Bundesnormen&Gesetzesnummer=10007517'
const ris = (
  <a href={GEWO_URL} target="_blank" rel="noopener noreferrer">
    ris.bka.gv.at
  </a>
)

const COPY = {
  de: {
    eyebrow: 'Rechtliches',
    title: 'Impressum',
    disclosure: 'Angaben gemäß § 5 E-Commerce-Gesetz (ECG), § 14 Unternehmensgesetzbuch (UGB) und § 25 Mediengesetz (MedienG)',
    details: [
      ['Firma', COMPANY.firm],
      ['Rechtsform', 'Gesellschaft mit beschränkter Haftung'],
      ['Sitz', 'Wien'],
      ['Anschrift', 'Gablenzgasse 21/GL/2-4, 1150 Wien, Österreich'],
      ['Firmenbuchnummer', COMPANY.fn],
      ['Firmenbuchgericht', 'Handelsgericht Wien'],
      ['UID-Nummer', COMPANY.vat],
      ['Geschäftsführer', COMPANY.director],
      [
        'Unternehmensgegenstand',
        'Entwicklung von Software, insbesondere Webplattformen, Apps, Dashboards und Schnittstellen, sowie UX/UI-Design, IT-Betrieb (DevOps) und IT-Beratung',
      ],
    ],
    contact: [
      ['Telefon', COMPANY.phone],
      ['E-Mail', COMPANY.email],
      ['Website', 'softmaze.at'],
    ],
    trade: [
      [
        'Gewerbeberechtigungen',
        'Dienstleistungen in der automatischen Datenverarbeitung und Informationstechnik; Unternehmensberatung einschließlich der Unternehmensorganisation',
      ],
      ['Gewerbebehörde', 'Magistratisches Bezirksamt für den 15. Bezirk'],
      [
        'Kammer',
        'Mitglied der Wirtschaftskammer Wien, Fachgruppe Unternehmensberatung, Buchhaltung und Informationstechnologie (UBIT)',
      ],
      ['Anwendbare Rechtsvorschriften', <>Gewerbeordnung (GewO), abrufbar unter {ris}</>],
    ] as Row[],
    media: 'Medieninhaber und Herausgeber: SoftMaze Solutions GmbH, Sitz Wien (Angaben siehe oben).',
    sections: [
      {
        heading: 'Haftungsausschluss',
        body: 'Die Inhalte dieser Website wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen. Als Diensteanbieter sind wir gemäß § 7 Abs. 1 ECG für eigene Inhalte auf diesen Seiten verantwortlich. Nach §§ 8 bis 10 ECG sind wir nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen. Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt. Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung möglich. Bei Bekanntwerden entsprechender Rechtsverletzungen werden wir diese Inhalte umgehend entfernen.',
      },
      {
        heading: 'Haftung für Links',
        body: 'Unsere Website enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich. Rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung nicht erkennbar. Eine permanente inhaltliche Kontrolle der verlinkten Seiten ist ohne konkrete Anhaltspunkte einer Rechtsverletzung nicht zumutbar. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Links umgehend entfernen.',
      },
      {
        heading: 'Urheberrecht',
        body: 'Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem österreichischen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechts bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers. Downloads und Kopien dieser Seite sind nur für den privaten, nicht kommerziellen Gebrauch gestattet.',
      },
    ],
  },
  en: {
    eyebrow: 'Legal',
    title: 'Imprint',
    disclosure: 'Information in accordance with § 5 E-Commerce Act (ECG), § 14 Commercial Code (UGB) and § 25 Media Act (MedienG)',
    details: [
      ['Company name', COMPANY.firm],
      ['Legal form', 'Limited liability company (GmbH)'],
      ['Registered office', 'Vienna'],
      ['Address', 'Gablenzgasse 21/GL/2-4, 1150 Vienna, Austria'],
      ['Company register number', COMPANY.fn],
      ['Register court', 'Commercial Court of Vienna (Handelsgericht Wien)'],
      ['VAT ID', COMPANY.vat],
      ['Managing director', COMPANY.director],
      [
        'Business purpose',
        'Software development, in particular web platforms, apps, dashboards and integrations, as well as UX/UI design, IT operations (DevOps) and IT consulting',
      ],
    ],
    contact: [
      ['Phone', COMPANY.phone],
      ['Email', COMPANY.email],
      ['Website', 'softmaze.at'],
    ],
    trade: [
      [
        'Trade licences',
        'IT services (Dienstleistungen in der automatischen Datenverarbeitung und Informationstechnik); management consulting including business organisation (Unternehmensberatung einschließlich der Unternehmensorganisation)',
      ],
      ['Trade authority', 'Municipal District Office for the 15th district (Magistratisches Bezirksamt für den 15. Bezirk)'],
      [
        'Chamber',
        'Member of the Vienna Chamber of Commerce (Wirtschaftskammer Wien), Professional Group for Management Consulting, Accounting and IT (UBIT)',
      ],
      ['Applicable law', <>Austrian Trade Regulation Act (Gewerbeordnung, GewO), available at {ris}</>],
    ] as Row[],
    media: 'Media owner and publisher: SoftMaze Solutions GmbH, registered office Vienna (details above).',
    sections: [
      {
        heading: 'Disclaimer',
        body: 'The content of this website has been created with great care. However, we cannot guarantee the accuracy, completeness, or timeliness of the content. As a service provider, we are responsible for our own content on these pages in accordance with § 7 Abs. 1 ECG. According to §§ 8 to 10 ECG, we are not obligated to monitor transmitted or stored third-party information or to search for indications of illegal activity. Obligations to remove or block the use of information under general law remain unaffected. Liability in this regard is only possible from the time we become aware of a specific legal violation. Upon notification of such violations, we will remove the content immediately.',
      },
      {
        heading: 'Liability for Links',
        body: 'Our website contains links to external websites of third parties, over whose content we have no control. Therefore, we cannot assume any liability for these external contents. The respective provider or operator of the pages is always responsible for the content of the linked pages. Illegal contents were not recognizable at the time of linking. Permanent monitoring of the linked pages is not reasonable without concrete evidence of a violation of law. Upon notification of such violations, we will remove such links immediately.',
      },
      {
        heading: 'Copyright',
        body: 'The content and works created by the site operator on these pages are subject to Austrian copyright law. Reproduction, editing, distribution, and any kind of utilization outside the limits of copyright law require the written consent of the respective author or creator. Downloads and copies of this site are permitted only for private, non-commercial use.',
      },
    ],
  },
}

function contactLink(value: string) {
  if (value === COMPANY.email) return <a href={`mailto:${value}`}>{value}</a>
  if (value === COMPANY.phone) return <a href={`tel:${value.replace(/\s/g, '')}`}>{value}</a>
  if (value === 'softmaze.at') return <a href="https://softmaze.at/">{value}</a>
  return value
}

export default function ImprintPage() {
  const locale = toLocale(useI18n().lang)
  const c = COPY[locale]
  useDocumentMeta({ ...PAGES['/imprint'][locale], path: '/imprint', locale })
  return (
    <ThemeProvider>
      <Header active="home" onNavigate={() => {}} />

      <main className="imprint-page">
        <div className="container imprint-content">
          <span className="eyebrow">{c.eyebrow}</span>
          <h1 className="imprint-title">{c.title}</h1>

          <section className="imprint-section">
            <h2>{c.disclosure}</h2>
            <ul>
              {c.details.map(([label, value]) => (
                <li key={label}>
                  <strong>{label}:</strong> {value}
                </li>
              ))}
            </ul>
            <ul>
              {c.trade.map(([label, value]) => (
                <li key={label}>
                  <strong>{label}:</strong> {value}
                </li>
              ))}
            </ul>
            <ul>
              {c.contact.map(([label, value]) => (
                <li key={label}>
                  <strong>{label}:</strong> {contactLink(value)}
                </li>
              ))}
            </ul>
            <p>{c.media}</p>

            {c.sections.map((s) => (
              <Fragment key={s.heading}>
                <h3>{s.heading}</h3>
                <p>{s.body}</p>
              </Fragment>
            ))}
          </section>
        </div>
      </main>

      <Footer />
    </ThemeProvider>
  )
}
